import React, { useEffect, useMemo, useState } from 'react';

import Drawer from '../../../components/shared/Drawer';
import DrawerNavigation from '../../../components/shared/DrawerNavigation';
import styled from 'styled-components';

import aampeTheme from '../../../constants/aampeTheme';
import { DrawerTabOptions, UNTITLED_MESSAGE } from '../../../constants/formula';

import { useNavContext } from '../../../context/NavContext';

import formatDate from 'helpers/dateTimeHelpers';
import { createShouldForwardProp } from 'helpers/shouldForwardProp';

import { useClickOutside } from 'hooks/helper/useClickOutside';

import {
    ApprovedFormulaState,
    FormulaDetailModel,
    FormulaSchedulingOptions,
} from 'models/formula.model';
import { TimingSlotsModel } from 'models/timing-slots.model';

import { DrawerActionMenu } from './DrawerActionMenu';
import PreviewCombinationsPanel from './PreviewCombinationsPanel';
import { FormulaTabs } from './tabs/FormulaTabs';

export type FormulaDrawerProps = {
    isOpen: boolean;
    onClose: () => void;
    formula: FormulaDetailModel;
    selectFormulaToClone: (value: number) => void;
    handleTestMessageOpen: () => void;
    openEditModalHandler: () => void;
    timingSlots?: TimingSlotsModel[];
    schedulingType: FormulaSchedulingOptions;
    setShowTimeSlotsModal: React.Dispatch<React.SetStateAction<boolean>>;
    refocusGrid: () => void;
    currentTab?: DrawerTabOptions;
    onTabChange?: (tab: DrawerTabOptions) => void;
    hasDeleteAccess: boolean;
    hasCreateFormulaAccess: boolean;
    templateName?: string;
    isCustomTemplate?: boolean;
    navigation?: {
        hasPrevious: boolean;
        hasNext: boolean;
        goToPrevious: () => void;
        goToNext: () => void;
    };
    showPreviewCombinations?: boolean;
    onTogglePreviewCombinations?: (show: boolean) => void;
};
const shouldForwardProps = createShouldForwardProp(['isLive']);

const DRAWER_WIDTH = '400px';
const COLLAPSED_NAV_WIDTH = '70px';

const FormulaDrawer: React.FC<FormulaDrawerProps> = ({
    isOpen,
    onClose,
    formula,
    selectFormulaToClone,
    handleTestMessageOpen,
    openEditModalHandler,
    timingSlots,
    schedulingType,
    setShowTimeSlotsModal,
    refocusGrid,
    currentTab,
    hasDeleteAccess,
    onTabChange,
    hasCreateFormulaAccess,
    templateName,
    isCustomTemplate,
    navigation,
    showPreviewCombinations: controlledShowPreviewCombinations,
    onTogglePreviewCombinations,
}) => {
    const [localShowPreviewCombinations, setLocalShowPreviewCombinations] =
        useState<boolean>(false);
    const showPreviewCombinations =
        controlledShowPreviewCombinations !== undefined
            ? controlledShowPreviewCombinations
            : localShowPreviewCombinations;

    const setShowPreviewCombinations =
        onTogglePreviewCombinations || setLocalShowPreviewCombinations;

    const { setForceCollapsed } = useNavContext();

    const handleTogglePreviewCombinations = () => {
        const newValue = !showPreviewCombinations;
        setShowPreviewCombinations(newValue);
        setForceCollapsed(newValue);
    };

    const handleClose = () => {
        setShowPreviewCombinations(false);
        setForceCollapsed(false);
        onClose();
        if (onTogglePreviewCombinations) {
            onTogglePreviewCombinations(false);
        }
    };

    const drawerRef = useClickOutside<HTMLDivElement>(handleClose, {
        active: isOpen,
        safeSelectors: ['.drawer-click-exempt', '.Toastify', '.side-nav'],
        closeOnEscape: true,
    });

    const drawerWidth = showPreviewCombinations
        ? `calc(100vw - ${COLLAPSED_NAV_WIDTH})`
        : DRAWER_WIDTH;

    const formattedDate = useMemo(
        () => formatDate(formula.updated_at),
        [formula.updated_at],
    );

    // Prevent body scroll when drawer is open
    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }

        // Clean up on unmount
        return () => {
            document.body.style.overflow = '';
        };
    }, [isOpen]);

    const isLive = formula.active_state === ApprovedFormulaState.Live;
    return (
        <div ref={drawerRef}>
            <Drawer
                isOpen={isOpen}
                onClose={handleClose}
                showOverlay={false}
                title="Preview"
                showCloseButton={true}
                width={drawerWidth}
                headerSlot={
                    navigation && (
                        <DrawerNavigation
                            onPrevious={navigation.goToPrevious}
                            onNext={navigation.goToNext}
                            hasPrevious={navigation.hasPrevious}
                            hasNext={navigation.hasNext}
                            showLabels={showPreviewCombinations}
                        />
                    )
                }
            >
                <DrawerContentWrapper>
                    <DrawerLeftPanel>
                        <HeaderPanel>
                            <Title>{formula.name || UNTITLED_MESSAGE}</Title>
                            <StatusRow>
                                <StatusDot
                                    isLive={isLive}
                                    data-testid="status-dot"
                                />
                                {formula.active_state} · Last Updated on{' '}
                                {formattedDate}
                            </StatusRow>
                        </HeaderPanel>
                        <DrawerActionMenu
                            data={formula}
                            onClose={handleClose}
                            selectFormulaToClone={selectFormulaToClone}
                            handleTestMessageOpen={handleTestMessageOpen}
                            openEditModalHandler={openEditModalHandler}
                            hasDeleteAccess={hasDeleteAccess}
                            hasCreateFormulaAccess={hasCreateFormulaAccess}
                            showPreviewCombinations={showPreviewCombinations}
                            onTogglePreviewCombinations={
                                handleTogglePreviewCombinations
                            }
                        />
                        <StyledDiv>
                            <FormulaTabs
                                data={formula}
                                timingSlots={timingSlots}
                                schedulingType={schedulingType}
                                setShowTimeSlotsModal={setShowTimeSlotsModal}
                                refocusGrid={refocusGrid}
                                currentTab={currentTab}
                                onTabChange={onTabChange}
                                templateName={templateName}
                                isCustomTemplate={isCustomTemplate}
                            />
                        </StyledDiv>
                    </DrawerLeftPanel>
                    {showPreviewCombinations && (
                        <PreviewPanelContainer>
                            <PreviewCombinationsPanel formula={formula} />
                        </PreviewPanelContainer>
                    )}
                </DrawerContentWrapper>
            </Drawer>
        </div>
    );
};

const DrawerContentWrapper = styled.div({
    display: 'flex',
    flexDirection: 'row',
    height: '100%',
    width: '100%',
});

const DrawerLeftPanel = styled.div({
    width: DRAWER_WIDTH,
    minWidth: DRAWER_WIDTH,
    maxWidth: DRAWER_WIDTH,
    height: '100%',
    overflowY: 'auto',
    display: 'flex',
    flexDirection: 'column',
});

const PreviewPanelContainer = styled.div({
    flex: 1,
    height: '100%',
    borderLeft: `1px solid ${aampeTheme.colors.interactiveWG01}`,
    overflow: 'hidden',
});

const HeaderPanel = styled.div({
    padding: aampeTheme.spacing4(6, 5, 5, 5),
    gap: aampeTheme.spacing4(2),
});
const Title = styled.h3({
    fontSize: aampeTheme.size.font.default,
    fontWeight: aampeTheme.weights.heavy,
    lineHeight: aampeTheme.size.lineHeight.default,
    fontStyle: 'normal',
    color: aampeTheme.colors.neutral09,
    wordBreak: 'break-word',
    whiteSpace: 'normal',
});

const StatusRow = styled.div({
    display: 'flex',
    alignItems: 'center',
    fontSize: aampeTheme.size.font.textMedium,
    color: aampeTheme.colors.textAccessibleWG01,
    marginTop: aampeTheme.spacing4(2),
    fontWeight: aampeTheme.weights.heavy,
});

const StatusDot = styled.div.withConfig({
    shouldForwardProp: shouldForwardProps,
})<{ isLive: boolean }>(({ isLive }) => ({
    height: aampeTheme.spacing4(2),
    width: aampeTheme.spacing4(2),
    borderRadius: '50%',
    display: 'inline-block',
    marginRight: aampeTheme.spacing4(1),
    backgroundColor: isLive
        ? aampeTheme.colors.green800
        : aampeTheme.colors.textAccessibleWG01,
}));
const StyledDiv = styled.div({
    marginLeft: aampeTheme.spacing4(1),
});
export default FormulaDrawer;
